// PERFORMANCE CONTRACT — this module is on the first-paint critical path of
// EVERY route (App.tsx -> AuthProvider -> here), so it must stay Firestore-free
// and must not START anything heavy at module scope (see App Check below).
// The Firestore SDK is ~1.5 MB of source (incl. its re2js dependency) and used
// to live here purely because `db` was exported alongside `auth`; that alone put
// ~150 KB gzip in front of first paint on auth-less entry pages like /signin and
// /pricing. Firestore now lives in `./firestoreDb`, which callers import
// statically (route chunks) or dynamically (useAuth). Do NOT re-add a
// `firebase/firestore` import here, and do not re-export `db` from this file.
import { initializeApp, getApps } from 'firebase/app'
import { getAuth, connectAuthEmulator } from 'firebase/auth'
import { initializeAppCheck, ReCaptchaV3Provider, type AppCheck } from 'firebase/app-check'
import { isPrerenderRuntime } from './prerender'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'wonderkit',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

// Prevent re-initialization during HMR
const app = getApps().length ? getApps()[0]! : initializeApp(firebaseConfig)

// Marketing prerendering deliberately runs without Firebase credentials: it
// captures only public routes and blocks Firebase network requests. Creating
// Auth with an empty build-time config throws before React can render the
// public page, so keep the Auth SDK dormant for that isolated runtime.
// `useAuth` also skips its Firebase listeners when this is true.
export const auth: ReturnType<typeof getAuth> | null = isPrerenderRuntime() ? null : getAuth(app)

// APP CHECK IS LAZY — reCAPTCHA is not on the first-paint path.
//
// `initializeAppCheck` pulls in the reCAPTCHA v3 loader (~156 KB of script the
// browser fetches from google.com before it can mint a token). Doing that at
// module scope meant every visitor to a marketing page paid for it during the
// render of a page that never calls an authenticated anything.
//
// CONTRACT: call `ensureAppCheck()` — idempotent, safe to await anywhere —
// before any request that carries an App Check token. Three chokepoints cover
// every path today, and nothing should reach Firebase outside them:
//   · every backend call, via `getAppCheckToken()` in lib/api/core.ts (this is
//     the funnel for ApiClient, SSE, analytics, and public/anchor fetches —
//     but NOT every /api/* call site: NewsletterCapture.tsx, useServerFeatures.ts,
//     and PublicKit.tsx's sendBeacon call raw fetch/sendBeacon directly and send
//     no App Check header. Pre-existing, not a regression of this contract;
//     sendBeacon in particular cannot await a token to attach one. Route new
//     backend calls through ApiClient rather than adding to this list;
//   · every Firebase Auth call, via `requireAuth()` below — the only supported
//     way to get a non-null `auth`. `useAuth` uses it for the wrapped sign-in /
//     sign-up / anonymous-link methods, and pages that call `firebase/auth`
//     directly (password reset, credential linking, email verification) get
//     App Check by construction because they need `requireAuth()` for the
//     instance. Do NOT reintroduce a local `auth ?? throw` guard: that is the
//     shape that silently bypassed App Check on /signin before.
//   · Firestore, via the top of lib/firestoreDb.ts — that module is imported
//     only by code that actually opens a listener, so it stays off the
//     landing path.
// `auth` is still exported for read-only inspection (e.g. `auth?.currentUser`
// in errorReporting) — reading it starts nothing, which is the point. The one
// place that narrows it without `requireAuth()` is `authForBootListeners()` in
// hooks/useAuth.ts: `getRedirectResult` and the `onIdTokenChanged` subscription
// run on every mount including the landing page, so they must NOT pull in
// reCAPTCHA; the branches there that really do an Auth exchange call
// `ensureAppCheck()` themselves.
// If App Check enforcement is ever turned on for the Firestore or
// Authentication products themselves (a Firebase console setting, separate
// from the backend's ENFORCE_APP_CHECK), re-check that list: a token refresh
// that Firebase performs on its own at boot for a returning session would not
// be preceded by any of these call sites.
//
// Skipped entirely during prerender: the Playwright browser would otherwise
// inject reCAPTCHA's iframe + `fire_app_check_[DEFAULT]` container into the
// DOM, which `page.content()` serializes into dist/index.html. That captured
// iframe's origin param points at the build-time localhost, so on a real
// user's device it fails to load, leaves App Check in a broken state, and
// every Firebase Auth request then fails with auth/network-request-failed.
let appCheckInstance: AppCheck | null = null
let appCheckStarted = false

export function ensureAppCheck(): AppCheck | null {
  if (appCheckStarted) return appCheckInstance
  appCheckStarted = true

  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_V3_SITE_KEY
  if (!recaptchaSiteKey || isPrerenderRuntime()) return null

  if (import.meta.env.DEV && import.meta.env.VITE_APPCHECK_DEBUG_TOKEN) {
    // @ts-expect-error Firebase reads this global for debug mode
    self.FIREBASE_APPCHECK_DEBUG_TOKEN = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN
  }
  appCheckInstance = initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(recaptchaSiteKey),
    isTokenAutoRefreshEnabled: true,
  })
  return appCheckInstance
}

/**
 * The one supported way to reach Firebase Auth: starts App Check (idempotent,
 * non-blocking — the token mints in the background) and returns a non-null
 * `auth`, throwing in the prerender runtime where Auth is deliberately dormant.
 *
 * Every caller that touches `firebase/auth` must go through this rather than
 * narrowing the exported `auth` itself, so App Check can never be skipped by
 * adding a new page.
 */
export function requireAuth(): NonNullable<typeof auth> {
  if (!auth) throw new Error('Firebase Auth is unavailable outside the prerender runtime')
  ensureAppCheck()
  return auth
}

/**
 * The App Check client if — and only if — something already started it.
 * Reading this never initializes anything; callers that need a token must go
 * through `ensureAppCheck()` (or `getAppCheckToken()`, which does).
 */
export function getAppCheckIfStarted(): AppCheck | null {
  return appCheckInstance
}

// Connect to the Auth emulator in local dev. The Firestore emulator hookup
// lives next to `db` in ./firestoreDb.
if (import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true') {
  // Guard against double-connection during HMR
  try {
    if (auth) connectAuthEmulator(auth, 'http://localhost:9110', { disableWarnings: true })
  } catch {
    // Already connected
  }
}

export default app
